import { useEffect, useRef, useState } from 'react';
import { useNotification } from '../../../contexts/NotificationsContext';
import { FeedbackMessages } from '../../../utils/commonVariables/messages';
import { analyzeCompetitor } from '../services/competitorServices';
import { Competitor } from '../types/competitors';
import { useAppConfigurationFeatures } from '../../../contexts/AppConfigurationContext';
import { useProcesses } from '../../../contexts/ProcessesContext';

interface CompetitorAnalysisOptions {
    hotelId: number;
    supplierHotelId: number;
}

/**
 * Enqueues the analysis of an existing competitor or a manual query.
 * The result is delivered later through an in-app notification.
 * @param options Numeric master hotel and supplier hotel IDs.
 * @returns Analysis action and shared loading state for both entry points.
 */
export const useCompetitorAnalysis = ({
    hotelId,
    supplierHotelId,
}: CompetitorAnalysisOptions) => {
    const { setErrorMessage, setSuccessMessage } = useNotification();
    const { zitoneo } = useAppConfigurationFeatures();
    const { startPolling } = useProcesses();
    const requestInProgress = useRef(false);
    const mountedRef = useRef(false);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [analyzingCompetitorId, setAnalyzingCompetitorId] = useState<
        string | null
    >(null);

    const analyze = async (input: Competitor | string): Promise<boolean> => {
        if (!mountedRef.current || requestInProgress.current) {
            return false;
        }
        requestInProgress.current = true;
        setIsAnalyzing(true);
        setAnalyzingCompetitorId(typeof input === 'string' ? null : input.id);
        try {
            const query =
                typeof input === 'string'
                    ? input.trim()
                    : [input.name, input.city, input.country]
                          .filter(Boolean)
                          .join(', ');
            if (
                !Number.isInteger(hotelId) ||
                hotelId <= 0 ||
                !Number.isInteger(supplierHotelId) ||
                supplierHotelId <= 0 ||
                !query
            ) {
                throw new Error(
                    'Valid hotel and supplier hotel IDs and a competitor are required.'
                );
            }
            const response = await analyzeCompetitor(
                hotelId,
                query,
                zitoneo.competitorScrapingPromptKey,
                supplierHotelId
            );
            if (!response.Result) {
                throw new Error('Competitor analysis could not be enqueued.');
            }
            startPolling();
            if (mountedRef.current) {
                setSuccessMessage('competitor_analysis_enqueued');
            }
            return true;
        } catch (error) {
            if (mountedRef.current) {
                console.error('Could not analyze competitor:', error);
                setErrorMessage(FeedbackMessages.COMMON_ERROR);
            }
            return false;
        } finally {
            requestInProgress.current = false;
            if (mountedRef.current) {
                setIsAnalyzing(false);
                setAnalyzingCompetitorId(null);
            }
        }
    };

    return { analyze, isAnalyzing, analyzingCompetitorId };
};
